import type Size from "@/util/Size.ts";
import type {MapTransform} from "@/encounter/model/ViewPort.ts";
import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import {PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";

export type WorldBounds = {left: number; top: number; right: number; bottom: number};

export function worldBoundsForViewport(size: Size, transform: MapTransform): WorldBounds {
    if (transform.scale <= 0) {
        return {left: -Infinity, top: -Infinity, right: Infinity, bottom: Infinity};
    }
    return {
        left: -transform.x / transform.scale,
        top: -transform.y / transform.scale,
        right: (size.width - transform.x) / transform.scale,
        bottom: (size.height - transform.y) / transform.scale,
    };
}

export function circleIntersectsBounds(
    center: {x: number; y: number}, radius: number, bounds: WorldBounds,
): boolean {
    return center.x + radius >= bounds.left && center.x - radius <= bounds.right
        && center.y + radius >= bounds.top && center.y - radius <= bounds.bottom;
}

export function effectIntersectsBounds(
    effect: AreaEffect, combatants: readonly Combatant[], bounds: WorldBounds,
): boolean {
    const center = combatants.find(combatant => combatant.id === effect.combatantId)?.position
        ?? effect.position;
    // A conservative radius also covers rotated rectangles and arcs.
    const radiusMeters = effect.type === "Rect"
        ? Math.hypot(effect.width ?? 0, effect.length ?? 0)
        : effect.radius ?? 0;
    return circleIntersectsBounds(center, radiusMeters * PIXELS_PER_METER, bounds);
}
